/**
 * Navigation per role — mirrors staticwebapp.config.json route rules (the server enforces the same).
 *
 * 1.5.22 §C: Admin is not in the side panel, nor in the phone's More sheet, for any role — it lives under the gear
 * (gear → Admin portal, src/app/GearMenu.tsx). The /admin routes and their guard (ROUTE_ROLES below) are untouched.
 */
import type { Role } from '@shared/roles';
import type { IconName } from '@/components/Icons';
import { MY_PROGRAMS } from '@shared/my-settings';
import { calMonthOf, firstKeyOf, isCalMonth } from '@shared/house-calendar';

/** The program strip's three (src/app/program.tsx PROGRAMS) and People's two more (PEOPLE_ONLY), kept here so the rule is pure. */
const PROGRAMS = MY_PROGRAMS;
type Program = (typeof MY_PROGRAMS)[number];

export interface NavItem {
  key: 'today' | 'lookup' | 'book' | 'planning' | 'calendar' | 'people' | 'volunteers' | 'events' | 'messages' | 'documents' | 'reports';
  to?: string;
  icon: IconName;
  roles: readonly Role[];
  /** Book opens a sheet instead of navigating. */
  action?: 'book';
}

const BOOKERS: readonly Role[] = ['admin', 'coadmin', 'scheduler'];
const STAFF: readonly Role[] = ['admin', 'coadmin'];

/**
 * 1.8.4 (D-1.8.4-1, D-1.8.4-2): **the top row is where you look; the side is what you do.** The side panel holds four
 * tools — Look up · Book · People · Volunteers — each for the roles that had it before. Today, Housekeeping, Companion,
 * Reading & Services and Events are the top row (src/app/AppShell.tsx ProgramStrip); Planning is inside Housekeeping
 * (its head row: Day · Planning · Schedule); the house Calendar is inside Today; Admin stays under the gear.
 *
 * Two keep a side-panel entry because they have no top row: the crew and the tech keep Today (their one screen, as
 * before), and the scheduler keeps the house Calendar (they have no Today tab to hold it — D-1.8.4-2).
 */
export const NAV: NavItem[] = [
  { key: 'today', to: '/', icon: 'today', roles: ['crew'] },
  { key: 'lookup', to: '/lookup', icon: 'lookup', roles: ['admin', 'coadmin', 'scheduler', 'viewer'] },
  // 1.8.5 (D-1.8.5-2): the tech books Tech Support tickets (and only those)
  { key: 'book', icon: 'plus', roles: [...BOOKERS, 'tech'], action: 'book' },
  { key: 'people', to: '/people', icon: 'people', roles: STAFF },
  { key: 'volunteers', to: '/volunteers', icon: 'volunteers', roles: STAFF },
  { key: 'calendar', to: '/calendar', icon: 'calendarEmpty', roles: ['scheduler', 'viewer'] },
  // 25 Oct 2026 (D-1025-NAV): Messages has a door of its own, read as "Messages, 3 waiting" (src/app/AppShell.tsx)
  { key: 'messages', to: '/messages', icon: 'mail', roles: BOOKERS },
  // 28 Oct 2026 (D-1028-FORMS part 3): the Documents library — blank forms, the office only
  { key: 'documents', to: '/documents', icon: 'file', roles: STAFF },
  // 1 Nov 2026 (D-1102-OFFICE-2): Reports — the monthly impact report for the Board and for grants; the office only
  { key: 'reports', to: '/reports', icon: 'printer', roles: STAFF },
];

/**
 * The phone tab bar: the same tools, Book in the middle (as before); More holds what does not fit, and Help & Support.
 * The crew's bar is Today alone, as before.
 */
export const TAB_KEYS = ['today', 'lookup', 'people', 'book', 'volunteers'] as const;
export const MORE_KEYS = ['messages', 'calendar', 'documents', 'reports'] as const;
const BAR_ROOM = 4;

/** The bar's tabs for a role: the first four of TAB_KEYS it may open. */
export function tabsFor(role: Role | null): NavItem[] {
  const mine = navFor(role);
  return TAB_KEYS.map((k) => mine.find((n) => n.key === k)).filter((n): n is NavItem => !!n).slice(0, BAR_ROOM);
}

/** Under More: what the role may open that is not in its bar. */
export function moreFor(role: Role | null): NavItem[] {
  const bar = new Set(tabsFor(role).map((n) => n.key));
  const mine = navFor(role);
  return MORE_KEYS.map((k) => mine.find((n) => n.key === k)).filter((n): n is NavItem => !!n && !bar.has(n.key));
}

/** 1.8.4 (D-1.8.4-1): the roles whose top row starts with Today — and who land on it on a fresh open. */
export const TODAY_TAB_ROLES: readonly Role[] = ['admin', 'coadmin'];
/** The roles whose top row is Housekeeping alone (the scheduler, and since 20 Oct 2026 the viewer); the crew and the tech have no top row. */
export const HOUSEKEEPING_ONLY_ROLES: readonly Role[] = ['scheduler', 'viewer', 'crew', 'tech'];
/** Housekeeping's own pages besides its day: they stand under the Housekeeping tab (its head row). */
export const HOUSEKEEPING_PAGES = ['/planning', '/planning/numbers', '/schedule'] as const;
export const isHousekeepingPage = (path: string) => (HOUSEKEEPING_PAGES as readonly string[]).includes(path);

export type TopTab = 'today' | Program | 'events' | 'tech';

/**
 * The top row (1.8.4, D-1.8.4-1): Today · Housekeeping · Companion · Reading & Services · Events for the administrator
 * and a co-administrator (the viewer: no Events — they cannot open it); Housekeeping alone for the scheduler; none for
 * the crew and the tech. Each tab is a place: pressing it goes there (Today and the programs on `/`, Events to
 * `/events`). People keeps this navigation row; its own filter is inside the page (1.8.11).
 */
export function topTabsFor(role: Role, _onPeople = false): TopTab[] {
  // 20 Oct 2026 (Eli): the viewer is Housekeeping only for now, like the scheduler
  if (role === 'scheduler' || role === 'viewer') return ['housekeeping'];
  // 1.8.5 (D-1.8.5-1): the tech's row is Tech Support alone, the way the scheduler's is Housekeeping; the crew has none
  if (role === 'tech') return ['tech'];
  if (role === 'crew') return [];
  const events = ROUTE_ROLES['/events']!.includes(role);
  const tech = ROUTE_ROLES['/tech']!.includes(role);
  return [...(TODAY_TAB_ROLES.includes(role) ? (['today'] as const) : []), ...PROGRAMS, ...(events ? (['events'] as const) : []), ...(tech ? (['tech'] as const) : [])];
}
/** Housekeeping's own pages besides its day; and (1.8.5) the Tech Support tab's: `/tech` and a ticket. */
export const isTechPage = (path: string) => /^\/tech(\/|$)/.test(path);

/**
 * 25 Oct 2026 (D-1025-NAV): each program has an address of its own. 26 Oct 2026 (Eli): `/` is always Today for the office
 * (administrator, co-administrator); the Housekeeping Day lives only at `/housekeeping`. The scheduler and the viewer, whose
 * one screen is the Day, are sent there from `/`; the crew and the tech keep `/` as their own home.
 */
export const PROGRAM_PATH: Record<Program, string> = { housekeeping: '/housekeeping', companion: '/companion', reading: '/reading' };
/** The program an address belongs to (Housekeeping's own pages count as Housekeeping); null for every other address. */
export function programOfPath(path: string): Program | null {
  for (const p of PROGRAMS) if (path === PROGRAM_PATH[p]) return p;
  return isHousekeepingPage(path) ? 'housekeeping' : null;
}
/** Where a top-row tab goes. */
export function topTabHref(tab: TopTab): string {
  return tab === 'today' ? '/' : tab === 'events' ? '/events' : tab === 'tech' ? '/tech' : PROGRAM_PATH[tab];
}
/** The tab lit for an address (null: a page of no program). Today is lit on `/` alone; Housekeeping on `/housekeeping` (and its own pages). */
export function currentTopTab(path: string, available: readonly TopTab[]): TopTab | null {
  if (/^\/events(\/|$)/.test(path) && available.includes('events')) return 'events';
  if (isTechPage(path) && available.includes('tech')) return 'tech';
  const byPath = programOfPath(path);
  if (byPath) return byPath;
  return path === '/' && available.includes('today') ? 'today' : null;
}

/** The query words only the Housekeeping Day reads (Today.tsx): a day, a close-out to open, the Book sheet. */
const HOUSEKEEPING_QUERY = ['day', 'closeout', 'book'] as const;

/**
 * 26 Oct 2026 (Eli): the old forms of `/` — `/?day=`, `/?closeout=`, `/?book=`, `/?tab=housekeeping|companion|reading|today` —
 * still work by moving to where they belong, query kept (`tab` removed). Returns null when `/` should simply show for the role:
 * the office's Today with no Housekeeping query, and the crew's and the tech's own home (a `tab` word is dropped there). The scheduler and the viewer (the Day is
 * their one screen) always move to `/housekeeping`.
 */
export function legacyHomeRedirect(role: Role | null, search: string): string | null {
  if (!role) return null;
  const q = new URLSearchParams(search);
  const tab = q.get('tab');
  q.delete('tab');
  const rest = q.toString() ? `?${q.toString()}` : '';
  if (role === 'crew' || role === 'tech') return tab ? `/${rest}` : null; // no tabs: the word is dropped, nothing else changes
  if (role === 'scheduler' || role === 'viewer') return `/housekeeping${rest}`;
  if ((PROGRAMS as readonly string[]).includes(tab ?? '')) return `${PROGRAM_PATH[tab as Program]}${rest}`;
  if (HOUSEKEEPING_QUERY.some((k) => q.has(k))) return `/housekeeping${rest}`;
  if (tab) return `/${rest}`; // ?tab=today (or a word nobody reads): Today, the word leaves the address
  return null;
}

/** Where `/calendar?m=` lands: the Day for that month's first day at `/housekeeping?day=<yyyyMMdd>`; this month, Today. */
export function calendarToTodayHref(m: string | null, todayKey: string): string {
  if (!m || !isCalMonth(m) || m === calMonthOf(todayKey)) return '/';
  return `/housekeeping?day=${firstKeyOf(m)}`;
}

/**
 * Help & Support (1.6.2): one door, the same words and the same icon wherever it is listed — the gear, for every
 * role, and the phone's More sheet. The words are the page's own title, never a second string.
 */
export const HELP_LINK = { to: '/help', icon: 'help', titleKey: 'help.title' } as const satisfies { to: string; icon: IconName; titleKey: string };

export function navFor(role: Role | null): NavItem[] {
  if (!role) return [];
  return NAV.filter((n) => n.roles.includes(role));
}

const SCHEDULE: readonly Role[] = ['admin', 'coadmin', 'scheduler', 'viewer'];

/** Which roles may open each route (client-side guard; the server has the final word). */
export const ROUTE_ROLES: Record<string, readonly Role[]> = {
  '/': ['admin', 'coadmin', 'scheduler', 'crew', 'tech', 'viewer'],
  /** A Tech Support ticket (1.5.0): the associate's Open ticket, and the office. 26 Oct 2026: not the scheduler (D-1020-SMALL: no /tech). */
  '/tech/:appointmentId': ['admin', 'coadmin', 'tech'],
  /** 1.8.5 (D-1.8.5-1): the Tech Support tab — Tickets · Workshops · Reports · Tools (20 Oct 2026: the viewer no longer — Housekeeping only). */
  '/tech': ['admin', 'coadmin', 'tech'],
  /** 25 Oct 2026 (D-1025-NAV): each program has an address of its own (Housekeeping's day; Companion and Reading & Services). */
  '/housekeeping': SCHEDULE,
  '/companion': STAFF,
  '/reading': STAFF,
  '/lookup': SCHEDULE,
  '/schedule': SCHEDULE,
  '/planning': SCHEDULE,
  /** 1.8.2 (D-1.8.2-3): the house calendar — the schedule roles (staticwebapp.config.json: NEEDS-ELI, not applied here). */
  '/calendar': SCHEDULE,
  /** Housekeeping in numbers (1.7.5): the charts of the schedule — whoever reads Planning. */
  '/planning/numbers': SCHEDULE,
  /** The Outbox: whoever can press Book or Send can take an email back. */
  '/messages': BOOKERS,
  '/people': STAFF,
  /** A person's history — reached from Look up, so it follows the schedule roles (staticwebapp.config.json /people/*). */
  '/people/:tenantId': SCHEDULE,
  /** Review visit (1.6.5): what happened to one visit — History's roles, and (1.6.7) the crew, read-only, for their
   * own crew's visits; the server decides which (staticwebapp.config.json /visits/*). */
  '/visits/:appointmentId': [...SCHEDULE, 'crew'],
  /** Record a home visit (1.5.11): whoever can open People. */
  '/people/:tenantId/home-visit': STAFF,
  '/volunteers': STAFF,
  /** One volunteer's page (18 Oct 2026, D-1018-VOL): the same roles as the list (staticwebapp.config.json /volunteers*). */
  '/volunteers/:volunteerId': STAFF,
  '/events': STAFF,
  /** 28 Oct 2026 (D-1028-FORMS part 3): the Documents library — blank forms; staticwebapp.config.json /documents* and /api/doc-library*. */
  '/documents': STAFF,
  /** 1 Nov 2026 (D-1102-OFFICE-2): the monthly impact report; staticwebapp.config.json /reports* and /api/impact-report*. */
  '/reports': STAFF,
  /** One event's page (1.7.0): the office works it; the scheduler reads it (staticwebapp.config.json /events/*). */
  '/events/:eventId': [...STAFF, 'scheduler'],
  /** The day-of roster (1.7.5): everyone expected, with Call — the office only (it reaches phone numbers). */
  '/events/:eventId/roster': STAFF,
  /** Surveys (1.7.3), under Events: the office only. 25 Oct 2026 (D-1025-NAV): closed to the scheduler (Housekeeping only);
   * staticwebapp.config.json says the same. */
  '/events/surveys': STAFF,
  '/events/surveys/:slug': STAFF,
  /** Announcements (1.7.6), under Events: the office writes and sends; closed to the scheduler since 25 Oct 2026 (D-1025-NAV). */
  /** The month's calendar (1.7.7), under Events: the office only (it makes, announces and requests). */
  '/events/month': STAFF,
  '/events/announcements': STAFF,
  '/events/announcements/:id': STAFF,
  '/admin': ['admin'],
  '/settings': ['admin', 'coadmin', 'scheduler', 'crew', 'tech', 'viewer'],
  /** Help & Support (the staff guide of 0.9.0), reached from the gear and from More (1.6.2). */
  '/help': ['admin', 'coadmin', 'scheduler', 'crew', 'tech', 'viewer'],
};
